const TRADE_DISPLAY_DEBUG_PARAM = 'tradeDisplayDebug'
const CHART_DEBUG_PARAM = 'chartDebug'
const TRADE_DISPLAY_PREFIX = '[TradeDisplay]'
const LICENSED_WIDGET_PREFIX = '[LicensedWidget]'

type DebugChannel = 'data' | 'fastForward' | 'drawing' | 'tournament' | 'reconciliation' | 'notification'

let focusedLoggingInstalled = false
let originalConsoleLog: typeof console.log | null = null
let originalConsoleInfo: typeof console.info | null = null
let originalConsoleDebug: typeof console.debug | null = null
let chartDebugEnabled: boolean | null = null

const hasWindow = (): boolean => typeof window !== 'undefined'

const allowFocusedLog = (args: unknown[]): boolean => {
  const [firstArg] = args
  return typeof firstArg === 'string' && firstArg.startsWith(TRADE_DISPLAY_PREFIX)
}

export const isTradeDisplayDebugEnabled = (search?: string): boolean => {
  if (!hasWindow() && search === undefined) {
    return false
  }

  const resolvedSearch = search ?? window.location.search
  return new URLSearchParams(resolvedSearch).get(TRADE_DISPLAY_DEBUG_PARAM) === 'true'
}

export const isChartDebugEnabled = (search?: string): boolean => {
  if (!hasWindow() && search === undefined) {
    return false
  }

  // Always read current search if explicitly provided; only cache implicit window.location.search
  if (search !== undefined) {
    return new URLSearchParams(search).get(CHART_DEBUG_PARAM) === 'true'
  }

  if (chartDebugEnabled !== null) {
    return chartDebugEnabled
  }

  const resolvedSearch = window.location.search
  chartDebugEnabled = new URLSearchParams(resolvedSearch).get(CHART_DEBUG_PARAM) === 'true'
  return chartDebugEnabled
}

export const installTradeDisplayLogFilter = (search?: string): void => {
  if (!hasWindow() || focusedLoggingInstalled || !isTradeDisplayDebugEnabled(search)) {
    return
  }

  originalConsoleLog = console.log.bind(console)
  originalConsoleInfo = console.info.bind(console)
  originalConsoleDebug = console.debug.bind(console)
  console.log = (...args: unknown[]) => {
    if (allowFocusedLog(args)) {
      originalConsoleLog?.(...args)
    }
  }
  console.info = (...args: unknown[]) => {
    if (allowFocusedLog(args)) {
      originalConsoleInfo?.(...args)
    }
  }
  console.debug = (...args: unknown[]) => {
    if (allowFocusedLog(args)) {
      originalConsoleDebug?.(...args)
    }
  }

  focusedLoggingInstalled = true
}

export const tradeDisplayLog = (message: string, details?: unknown): void => {
  if (!isTradeDisplayDebugEnabled()) {
    return
  }

  const label = `${TRADE_DISPLAY_PREFIX} ${message}`
  if (details === undefined) {
    console.log(label)
    return
  }

  console.log(label, JSON.stringify(details))
}

export const licensedWidgetLog = (message: string, details?: unknown): void => {
  const label = `${LICENSED_WIDGET_PREFIX} ${message}`
  if (details === undefined) {
    console.error(label)
    return
  }

  console.error(label, JSON.stringify(details))
}

export const chartDebugLog = (channel: DebugChannel, message: string, summary?: Record<string, unknown>): void => {
  if (!isChartDebugEnabled()) {
    return
  }

  const label = `[Chart:${channel}] ${message}`
  if (summary === undefined) {
    console.debug(label)
    return
  }

  console.debug(label, summary)
}
